import { Loader2 } from "lucide-react";
import { useState } from "react";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useImpersonation } from "@/hooks/use-impersonation";
import { useUsers } from "@/hooks/use-users";
import { useSession } from "@/lib/auth-client";
import { ConfirmViewAsElevatedDialog, viewAsNeedsConfirming } from "./ConfirmViewAsElevatedDialog";

/**
 * Pick the account to view as. Lists login ACCOUNTS (not people) because a
 * person with no login has nothing to view as; the list is the same
 * server-searched /api/users read the Users admin table uses. Your own row is
 * hidden — the server refuses it anyway.
 */
export function ViewAsDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: session } = useSession();
  const [search, setSearch] = useState("");
  const debounced = useDebouncedValue(search, 250);
  const users = useUsers(
    { search: debounced || undefined, sort: "name", dir: "asc" },
    { cursor: null, direction: "next" },
  );
  const { start, isPending } = useImpersonation();
  const [startingId, setStartingId] = useState<string | null>(null);
  // Held rather than started immediately when the target holds admin access —
  // see `ConfirmViewAsElevatedDialog`. Null for everyone else, who start on the
  // first click as they always did.
  const [confirming, setConfirming] = useState<{
    id: string;
    name: string;
    role: string | null;
  } | null>(null);

  const rows = (users.data?.data ?? []).filter((u) => u.id !== session?.user?.id && !u.banned);

  function begin(user: { id: string; name: string; role: string | null }) {
    if (viewAsNeedsConfirming(user.role)) {
      setConfirming(user);
      return;
    }
    setStartingId(user.id);
    void start(user.id);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 sm:max-w-md">
        <DialogHeader className="px-4 pt-4">
          <DialogTitle>View as…</DialogTitle>
          <DialogDescription>
            See Enable exactly as this person does. Anything you change is recorded under your name.
          </DialogDescription>
        </DialogHeader>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by name or email"
            value={search}
            onValueChange={setSearch}
            autoFocus
          />
          <CommandList>
            {users.isLoading && (
              <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                <Loader2 className="mr-2 size-4 animate-spin" /> Loading…
              </div>
            )}
            {users.isError && <CommandEmpty>Couldn't load accounts.</CommandEmpty>}
            {!users.isLoading && !users.isError && rows.length === 0 && (
              <CommandEmpty>No accounts match.</CommandEmpty>
            )}
            {rows.map((u) => (
              <CommandItem
                key={u.id}
                value={u.id}
                disabled={isPending}
                onSelect={() => {
                  begin({ id: u.id, name: u.name, role: u.role });
                }}
              >
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate">{u.name}</span>
                  <span className="truncate text-xs text-muted-foreground">{u.email}</span>
                </div>
                {startingId === u.id && isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : null}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </DialogContent>
      {confirming !== null && (
        <ConfirmViewAsElevatedDialog
          open
          onOpenChange={(next) => {
            if (!next) setConfirming(null);
          }}
          name={confirming.name}
          role={confirming.role}
          onConfirm={() => {
            setStartingId(confirming.id);
            void start(confirming.id);
            setConfirming(null);
          }}
        />
      )}
    </Dialog>
  );
}
